import axios from 'axios'

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1']
if (process.env.REACT_APP_LOCAL_IP) {
  LOCAL_HOSTNAMES.push(process.env.REACT_APP_LOCAL_IP)
}

const isLocalhost = LOCAL_HOSTNAMES.includes(window.location.hostname)

const API_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalhost
    ? 'http://localhost:8090/api'
    : process.env.REACT_APP_API_URL_PROD ||
      'https://tribulum.redirectme.net/api')

// ── Token en memoria del módulo (no en localStorage ni sessionStorage) ────────
// Más seguro que localStorage frente a extensiones maliciosas.
// Se pierde al recargar la página; la persistencia entre recargas depende de la
// cookie httpOnly de refresh token (funciona en same-site; en cross-site puede
// ser bloqueada por políticas de terceros del navegador).
let _accessToken = null

export const setAccessToken = (token) => {
  _accessToken = token
}

export const clearAccessToken = () => {
  _accessToken = null
}

export const getAccessToken = () => _accessToken

// ── Cliente axios ─────────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // envía cookies httpOnly como capa adicional cuando el navegador las admita
  headers: {
    'Content-Type': 'application/json',
  },
})

let refreshPromise = null

const refreshAccessToken = async () => {
  // withCredentials envía la cookie refresh_token si el navegador la admite.
  // El servidor devuelve el nuevo access token también en el cuerpo (mecanismo dual)
  // para entornos cross-site donde las cookies de terceros están bloqueadas.
  const response = await apiClient.post('/auth/refresh')
  const { accessToken } = response.data
  if (accessToken) setAccessToken(accessToken)
  return response.data
}

// ── Interceptor de petición: añade cabecera Authorization ─────────────────────
apiClient.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`
  }
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[API] -> ${config.method?.toUpperCase()} ${config.url}`, { data: config.data })
  }
  return config
})

// ── Interceptor de respuesta: reintento con refresh en 401 ────────────────────
apiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        `[API] <- ${response.config.method?.toUpperCase()} ${response.config.url}`,
        { status: response.status },
      )
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `[API] Error en ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}:`,
        { status: error.response?.status },
      )
    }

    // Reintentar una vez con refresh token si recibimos 401 (no en rutas de auth)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/')
    ) {
      originalRequest._retry = true
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }
        await refreshPromise
        // El interceptor de request añadirá la nueva cabecera Authorization en el reintento
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('[API] Error al refrescar token:', refreshError)
        clearAccessToken()
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

// Exportaciones de compatibilidad (sin efecto, evitan romper importaciones existentes)
export const setRememberCache = () => {}
export const invalidateTokenCache = () => {}

export default apiClient
